import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, X, Download } from 'lucide-react';
import { toPng } from 'html-to-image';
import { transformations, Transformation } from '../character/content/transformations';
import { TransformationCard } from '../character/cards/TransformationSheet';
import { useContentVersion } from '../hooks/ContentVersionContext';
import { pickVersion } from '../character/content/sources';
import { fuzzyFilter } from '../search/fuzzySearch';
import { useStrings } from '../locales';
import { SwipeHint } from '../components/CardSheet';
import '../components/CardSheet.css';
import './Transformations.css';

const INTRO_PARAGRAPHS = [
  "Transformations represent changes or augmentations to characters in Daggerheart. These are optional aspects of a character's identity that may be given out by the GM during a campaign for narrative purposes. GMs may also present transformations as an option at character creation, at their discretion.",
  "Transformations provide both a mechanical benefit and a drawback. As a player, you are responsible for ensuring you track both features, paying special attention to the drawback so that the GM isn't required to track it for you. Utilizing transformation cards requires you to buy into the concept of the exchange, taking on the burden to also reap the benefit.",
  "When you gain a transformation, add the card to your loadout as if it were an aspect of your heritage. Like ancestry and community cards, it does not count against your domain card limit. Consider how this change augments your character both physically and psychologically. Use the transformation questions in the applicable section to explore its affects on your character, or make up your own.",
  "You can only have one transformation per PC.",
  "As a GM, we recommend you discuss the possibility of including transformations at your table before introducing them to the players. Because they are optional and deeply impact the PCs' narrative journey, ensure they are something they will enjoy, and that the transformations support the story they want to tell.",
  "Some transformations have rather obvious origin points in a campaign — the vampire or werewolf transformation could come from a particularly important attack from a major villain in your campaign. Perhaps the reanimated or ghost might come about because a PC dies in a place of great magic or another PC makes a story-altering bargain in exchange for their life to be restored. Other transformations are more likely to be taken at character creation, like the shapeshifter or demigod, but could still be introduced as a character twist during a campaign. It's ultimately up to you, as the GM, which transformations are available to players at character creation and how they become available throughout play, so you can decide what works best for you, your players, and the story you're telling together.",
];

function TransformationModal({ items, idx, onClose, onNav }: {
  items: Transformation[];
  idx: number;
  onClose: () => void;
  onNav: (i: number) => void;
}) {
  const t = items[idx];
  const cardRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef(0);
  const isDragging = useRef(false);

  const prev = useCallback(() => onNav((idx - 1 + items.length) % items.length), [idx, items.length, onNav]);
  const next = useCallback(() => onNav((idx + 1) % items.length), [idx, items.length, onNav]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    isDragging.current = true;
    if (cardRef.current) cardRef.current.style.transition = 'none';
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging.current || !cardRef.current) return;
    const dx = e.touches[0].clientX - touchStartX.current;
    cardRef.current.style.transform = `translateX(${Math.max(-60, Math.min(60, dx))}px)`;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!isDragging.current || !cardRef.current) return;
    isDragging.current = false;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    cardRef.current.style.transition = '';
    cardRef.current.style.transform = '';
    if (dx < -60) next();
    else if (dx > 60) prev();
  };

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
      if (e.key === 'ArrowRight') { e.preventDefault(); next(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, prev, next]);

  const handleDownload = useCallback(async () => {
    const wrapper = cardRef.current;
    if (!wrapper) return;
    const card = wrapper.querySelector<HTMLElement>('.portrait-card') ?? wrapper;
    try {
      const url = await toPng(card, { pixelRatio: Math.max(window.devicePixelRatio, 2) });
      const a = document.createElement('a');
      a.download = `${t.name.replace(/\s+/g, '-').toLowerCase()}-transformation.png`;
      a.href = url; a.click();
    } finally {}
  }, [t, cardRef]);

  return (
    <div className="card-modal-overlay" onClick={onClose}>
      <button className="card-nav-btn card-nav-btn--prev" onClick={(e) => { e.stopPropagation(); prev(); }} aria-label="Previous">
        <ChevronLeft size={22} />
      </button>
      <SwipeHint />
      <div ref={cardRef} className="card-modal-card-wrapper" onClick={(e) => e.stopPropagation()} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
        <TransformationCard t={t} />
        <button className="card-sheet__close" onClick={onClose} aria-label="Close"><X size={14} /></button>
      </div>
      <button className="card-download-btn" onClick={(e) => { e.stopPropagation(); handleDownload(); }} aria-label="Download card" title="Download card">
        <Download size={16} /> Download
      </button>
      <button className="card-nav-btn card-nav-btn--next" onClick={(e) => { e.stopPropagation(); next(); }} aria-label="Next">
        <ChevronRight size={22} />
      </button>
    </div>
  );
}

export default function Transformations() {
  const s = useStrings().transformations;
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(() => searchParams.get('q') || '');

  useEffect(() => {
    const q = searchParams.get('q');
    if (q) setSearch(q);
  }, [searchParams]);
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  const [version] = useContentVersion();

  const filtered = fuzzyFilter(
    pickVersion(transformations, version),
    search,
    (t) => [t.subtitle, t.benefit.name, t.benefit.text as string, t.drawback.name, t.drawback.text as string].join(' '),
    (t) => t.name
  );

  return (
    <div className="page-content">
      <div className="page-header">
        <h1>{s.title}</h1>
        <p>{s.desc}</p>
      </div>

      <div className="filter-bar" style={{ marginBottom: '20px' }}>
        <input
          type="text"
          placeholder="Search transformations…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="ancestry-search"
          aria-label="Search transformations"
        />
      </div>

      <div className="tf-grid">
        {filtered.map((t, i) => (
          <button key={t.name} className={`card-btn${search && i === 0 ? ' card-btn--highlight' : ''}`} onClick={() => setOpenIdx(i)} aria-label={`Open ${t.name}`}>
            <TransformationCard t={t} />
          </button>
        ))}
      </div>

      {openIdx !== null && (
        <TransformationModal items={filtered} idx={openIdx} onClose={() => setOpenIdx(null)} onNav={setOpenIdx} />
      )}

      <div className="divider" />

      <div className="tf-lore">
        <h2 className="tf-lore__heading">Transformation Cards</h2>
        {INTRO_PARAGRAPHS.map((p, i) => (
          <p key={i} className="tf-lore__para">{p}</p>
        ))}
      </div>
    </div>
  );
}
